import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Avatar,
  Collapse,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { CSSObject, styled, Theme, useTheme } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import Toolbar from "@mui/material/Toolbar";
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import crmLogo from "../../assets/crm-logo.svg";
import crmNav01 from "../../assets/crm-sidenav/crm-nav-01.svg";
import crmNav02 from "../../assets/crm-sidenav/crm-nav-02.svg";
import crmNav03 from "../../assets/crm-sidenav/crm-nav-03.svg";
import crmNav04 from "../../assets/crm-sidenav/crm-nav-04.svg";
import crmNav05 from "../../assets/crm-sidenav/crm-nav-05.svg";
import crmNav06 from "../../assets/crm-sidenav/crm-nav-06.svg";
import crmNav07 from "../../assets/crm-sidenav/crm-nav-07.svg";
import crmNav08 from "../../assets/crm-sidenav/crm-nav-08.svg";
import crmNav09 from "../../assets/crm-sidenav/crm-nav-09.svg";
import AccessDenied from "../../components/AccessDenied";
import Authentication from "../../components/Authentication";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { viewImage } from "../../services/upload.service";
import { useAuthStore, useCurrentPageStore } from "../../stores/store";
import { AuthenStoreType } from "../../types/authen";
import { InitialUserPageType } from "../../types/global";

const drawerWidth = 272;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

type SubMenuType = {
  text: string;
  to: string;
  show?: boolean;
};

type MenuType = {
  text: string;
  iconSrc: string;
  to: string;
  show?: boolean;
  children?: SubMenuType[];
};

type BreadcrumbItem = {
  label: string;
  to: string;
};

const getDefaultBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  if (pathname === "/crm" || pathname === "/crm/customers") {
    return [{ label: "Customer List", to: "/crm/customers" }];
  }
  if (/^\/crm\/customers\/[^/]+$/.test(pathname)) {
    return [
      { label: "Customer List", to: "/crm/customers" },
      { label: "Customer Detail", to: "" },
    ];
  }
  if (pathname === "/crm/locations") {
    return [{ label: "Address Approval", to: "/crm/locations" }];
  }
  if (pathname === "/crm/orders") {
    return [{ label: "Order List", to: "/crm/orders" }];
  }
  if (/^\/crm\/orders\/[^/]+\/edit$/.test(pathname)) {
    return [
      { label: "Order List", to: "/crm/orders" },
      { label: "Edit Order", to: "" },
    ];
  }
  if (pathname === "/crm/product-master") {
    return [{ label: "Product Master", to: "/crm/product-master" }];
  }
  if (pathname === "/crm/product-master/create") {
    return [
      { label: "Product Master", to: "/crm/product-master" },
      { label: "Create Product Master", to: "" },
    ];
  }
  if (/^\/crm\/inventory\/[^/]+\/edit$/.test(pathname)) {
    return [
      { label: "Product Master", to: "/crm/product-master" },
      { label: "Edit Product Master", to: "" },
    ];
  }
  if (pathname === "/crm/integrations") {
    return [{ label: "Integrations", to: "/crm/integrations" }];
  }
  if (pathname === "/crm/privileges") {
    return [{ label: "Privilege List", to: "/crm/privileges" }];
  }
  if (pathname === "/crm/privileges/create") {
    return [
      { label: "Privilege List", to: "/crm/privileges" },
      { label: "Create Privilege", to: "" },
    ];
  }
  if (/^\/crm\/privileges\/[^/]+\/edit$/.test(pathname)) {
    return [
      { label: "Privilege List", to: "/crm/privileges" },
      { label: "Edit Privilege", to: "" },
    ];
  }
  if (pathname === "/crm/redemptions") {
    return [{ label: "Redemption Tracking", to: "/crm/redemptions" }];
  }
  if (pathname === "/crm/addons") {
    return [{ label: "Add-on List", to: "/crm/addons" }];
  }
  if (pathname === "/crm/addons/create") {
    return [
      { label: "Add-on List", to: "/crm/addons" },
      { label: "Create Add-on", to: "" },
    ];
  }
  if (/^\/crm\/addons\/[^/]+\/edit$/.test(pathname)) {
    return [
      { label: "Add-on List", to: "/crm/addons" },
      { label: "Edit Add-on", to: "" },
    ];
  }
  if (pathname === "/crm/reports") {
    return [{ label: "Reports", to: "/crm/reports" }];
  }
  if (pathname === "/crm/help") {
    return [{ label: "FAQ Management", to: "/crm/help" }];
  }

  return [{ label: "CRM", to: "/crm" }];
};

export default function Crm() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const { breadcrumbs, setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs([]);
  }, [location.pathname, setBreadcrumbs]);
  const { user: auth, clearAuthState } = useAuthStore(
    (state) => state as AuthenStoreType,
  );
  const setCurrentPage = useCurrentPageStore(
    (state) => (state as InitialUserPageType).setCurrentPageState,
  );
  const { clearCurrentPageState } = useCurrentPageStore(
    (state) => state as InitialUserPageType,
  );
  const hasCrm = auth?.duties?.includes("crm");
  const hasReward = auth?.duties?.includes("reward");

  const menus: MenuType[] = [
    {
      text: "Customer List",
      iconSrc: crmNav01,
      to: "/crm",
      show: hasCrm,
    },
    {
      text: "Address Approval",
      iconSrc: crmNav02,
      to: "/crm/locations",
      show: false,
    },
    {
      text: "Order List",
      iconSrc: crmNav03,
      to: "/crm/orders",
      show: hasCrm,
    },
    {
      text: "Product Master",
      iconSrc: crmNav04,
      to: "/crm/product-master",
      show: hasCrm,
    },
    {
      text: "Add-on",
      iconSrc: crmNav07,
      to: "/crm/addons",
      show: hasCrm,
    },
    {
      text: "Privileges",
      iconSrc: crmNav05,
      to: "/crm/privileges",
      show: hasReward,
      children: [
        {
          text: "Manage Privileges",
          to: "/crm/privileges",
          show: hasReward,
        },
        {
          text: "Redemption Tracking",
          to: "/crm/redemptions",
          show: hasReward,
        },
      ],
    },
    {
      text: "FAQ Management",
      iconSrc: crmNav08,
      to: "/crm/help",
      show: hasCrm || hasReward,
    },
  ];

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    return initial;
  });

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!open) {
      setExpandedMenus(new Set());
    }
  }, [open]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSignOut = () => {
    clearAuthState();
    clearCurrentPageState();
    navigate("/", { replace: true });
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      if (!isClosing) {
        setMobileOpen(!mobileOpen);
      }
    } else {
      setOpen(!open);
    }
  };

  const handleClickMenu = (menu: MenuType) => {
    if (menu.children && menu.children.length > 0) {
      if (!open && !isMobile) {
        setOpen(true);
        setExpandedMenus((prev) => new Set(prev).add(menu.text));
        return;
      }
      setExpandedMenus((prev) => {
        const next = new Set(prev);
        if (next.has(menu.text)) {
          next.delete(menu.text);
        } else {
          next.add(menu.text);
        }
        return next;
      });
      return;
    }
    if (isMobile) handleDrawerToggle();
    setCurrentPage(menu?.text ?? "");
    navigate(menu.to);
  };

  const handleClickSubMenu = (sub: SubMenuType) => {
    if (isMobile) handleDrawerToggle();
    setCurrentPage(sub.text);
    navigate(sub.to);
  };

  const isActiveMenu = (menu: MenuType) => {
    if (menu.children && menu.children.length > 0) {
      return menu.children.some((child) => location.pathname.startsWith(child.to));
    }
    if (menu.to === "/crm") {
      return (
        location.pathname === "/crm" || location.pathname === "/crm/customers"
      );
    }

    return location.pathname.startsWith(menu.to);
  };

  const displayBreadcrumbs = useMemo(() => {
    if (breadcrumbs.length > 0) return breadcrumbs;
    return getDefaultBreadcrumbs(location.pathname);
  }, [breadcrumbs, location.pathname]);

  const pageTitle = useMemo(() => {
    const lastBreadcrumb = displayBreadcrumbs[displayBreadcrumbs.length - 1];
    return lastBreadcrumb?.label || "CRM";
  }, [displayBreadcrumbs]);

  const drawer = (
    <List className="pt-[88px]">
      {menus.filter((menu) => menu.show).map((menu, index) => (
        <React.Fragment key={`menu-${index}`}>
          <ListItem
            disablePadding
            sx={{ display: "block" }}
          >
            <ListItemButton
              onClick={() => handleClickMenu(menu)}
              selected={isActiveMenu(menu)}
              sx={[
                {
                  minHeight: 48,
                },
                open
                  ? {
                      justifyContent: "initial",
                      px: 2,
                    }
                  : {
                      justifyContent: "center",
                      px: 1,
                    },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                  },
                  isMobile
                    ? {
                        mr: 2,
                      }
                    : open
                      ? {
                          mr: 2,
                        }
                      : {
                          mr: "auto",
                          ml: "auto",
                        },
                ]}
              >
                <Box
                  component="img"
                  src={menu.iconSrc}
                  alt=""
                  sx={{ width: 40, height: 40, display: "block" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={menu.text}
                slotProps={{
                  primary: {
                    fontSize: theme.typography.body2.fontSize,
                  },
                }}
                sx={[
                  isMobile
                    ? {
                        display: "block",
                      }
                    : open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                          display: "none",
                        },
                ]}
              />
              {menu.children && menu.children.length > 0 && (open || isMobile) && (
                expandedMenus.has(menu.text) ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
          </ListItem>
          {menu.children && menu.children.length > 0 && (open || isMobile) && (
            <Collapse in={expandedMenus.has(menu.text)} timeout="auto" unmountOnExit>
              <List disablePadding>
                {menu.children.filter((sub) => sub.show).map((sub, subIndex) => (
                  <ListItemButton
                    key={`sub-${index}-${subIndex}`}
                    onClick={() => handleClickSubMenu(sub)}
                    selected={location.pathname.startsWith(sub.to)}
                    sx={[
                      {
                        minHeight: 40,
                        pl: open || isMobile ? 9 : 1,
                        justifyContent: open || isMobile ? "initial" : "center",
                      },
                    ]}
                  >
                    <ListItemText
                      primary={sub.text}
                      slotProps={{
                        primary: {
                          fontSize: theme.typography.body2.fontSize,
                        },
                      }}
                      sx={[
                        isMobile
                          ? { display: "block" }
                          : open
                            ? { opacity: 1 }
                            : { opacity: 0, display: "none" },
                      ]}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <Authentication>
      <Box className="flex">
        <AppBar position="fixed" open={open} className="flex justify-center h-16">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                mr: 2,
              }}
            >
              <MenuIcon className="text-white" />
            </IconButton>
            <div className="flex-grow flex items-center">
              <img src={crmLogo} alt="Essilor Luxottica" className="h-[14px]" />
            </div>
            {auth && (
              <div>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar
                    src={auth?.image ? viewImage(auth?.image) : undefined}
                  />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 272,
                      },
                    },
                  }}
                >
                  <MenuItem>
                    <Stack
                      direction="row"
                      spacing={4}
                      useFlexGap
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={auth?.image ? viewImage(auth?.image) : undefined}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body1">{auth?.name}</Typography>
                        <Typography
                          variant="body2"
                          color="grey.400"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {auth?.role}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => navigate("/portal", { replace: true })}
                  >
                    <Typography variant="body2">Change Duty</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => onSignOut()}>
                    <Typography variant="body2">Sign Out</Typography>
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
        </AppBar>
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
            },
            "& .MuiPaper-root": {
              paddingTop: "88px",
            },
          }}
        >
          {drawer}
        </MuiDrawer>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          {drawer}
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            py: 3,
            px: { xs: 1, md: 2 },
            transition: (theme) =>
              theme.transitions.create(["width", "margin"], {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            width: {
              xs: "100%",
              md: open
                ? `calc(100% - ${drawerWidth}px)`
                : `calc(100% - ${65}px)`,
            },
          }}
        >
          <DrawerHeader />
          <div>
            <CustomBreadcrumbs className="mb-2" items={displayBreadcrumbs} />
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: "1.5rem",
              }}
            >
              {pageTitle}
            </Typography>
            {!hasCrm && !hasReward ? <AccessDenied /> : <Outlet />}
          </div>
        </Box>
      </Box>
    </Authentication>
  );
}
